import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Palette, 
  Upload, 
  Save,
  Eye,
  Image as ImageIcon,
  Globe,
  Phone,
  Mail,
  MapPin,
  MessageSquare,
  PanelLeft,
  Sparkles,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// COMPLETE THEME PRESETS - All colors in one preset
const COMPLETE_THEME_PRESETS = [
  {
    name: 'Professional Dark',
    value: 'professional_dark',
    description: 'Profesional & modern untuk bisnis',
    icon: '🌑',
    primary: '#0EA5E9',
    secondary: '#06B6D4',
    sidebar_color: '#0f172a',
    sidebar_gradient_start: '#0f172a',
    sidebar_gradient_end: '#1e3a5f',
    sidebar_use_gradient: true,
    content_bg_color: '#f8fafc',
    content_bg_gradient_start: '#f8fafc',
    content_bg_gradient_end: '#e2e8f0',
    content_use_gradient: true
  },
  {
    name: 'Fresh Green',
    value: 'fresh_green',
    description: 'Segar & natural',
    icon: '🌲',
    primary: '#059669',
    secondary: '#10B981',
    sidebar_color: '#14532d',
    sidebar_gradient_start: '#14532d',
    sidebar_gradient_end: '#166534',
    sidebar_use_gradient: true,
    content_bg_color: '#f0fdf4',
    content_bg_gradient_start: '#f0fdf4',
    content_bg_gradient_end: '#dcfce7',
    content_use_gradient: true
  },
  {
    name: 'Elegant Purple',
    value: 'elegant_purple',
    description: 'Elegan & premium',
    icon: '👑',
    primary: '#9333EA',
    secondary: '#C084FC',
    sidebar_color: '#2e1065',
    sidebar_gradient_start: '#2e1065',
    sidebar_gradient_end: '#4c1d95',
    sidebar_use_gradient: true,
    content_bg_color: '#faf5ff',
    content_bg_gradient_start: '#faf5ff',
    content_bg_gradient_end: '#f3e8ff',
    content_use_gradient: true
  },
  {
    name: 'Ocean Blue',
    value: 'ocean_blue',
    description: 'Trustworthy & teknologi',
    icon: '🌊',
    primary: '#0EA5E9',
    secondary: '#38BDF8',
    sidebar_color: '#0c4a6e',
    sidebar_gradient_start: '#0c4a6e',
    sidebar_gradient_end: '#0369a1',
    sidebar_use_gradient: true,
    content_bg_color: '#f0f9ff',
    content_bg_gradient_start: '#f0f9ff',
    content_bg_gradient_end: '#e0f2fe',
    content_use_gradient: true
  },
  {
    name: 'Dark Mode',
    value: 'dark_mode',
    description: 'Modern & hemat mata',
    icon: '🌙',
    primary: '#3B82F6',
    secondary: '#60A5FA',
    sidebar_color: '#171717',
    sidebar_gradient_start: '#171717',
    sidebar_gradient_end: '#262626',
    sidebar_use_gradient: true,
    content_bg_color: '#0f172a',
    content_bg_gradient_start: '#0f172a',
    content_bg_gradient_end: '#1e293b',
    content_use_gradient: true
  },
  {
    name: 'Warm Sunset',
    value: 'warm_sunset',
    description: 'Hangat & energik',
    icon: '🌅',
    primary: '#F97316',
    secondary: '#FB923C',
    sidebar_color: '#7c2d12',
    sidebar_gradient_start: '#7c2d12',
    sidebar_gradient_end: '#9a3412',
    sidebar_use_gradient: true,
    content_bg_color: '#fffbeb',
    content_bg_gradient_start: '#fffbeb',
    content_bg_gradient_end: '#fef3c7',
    content_use_gradient: true
  },
  {
    name: 'Rose Pink',
    value: 'rose_pink',
    description: 'Cerah & energik',
    icon: '🌸',
    primary: '#E11D48',
    secondary: '#F43F5E',
    sidebar_color: '#4c0519',
    sidebar_gradient_start: '#4c0519',
    sidebar_gradient_end: '#881337',
    sidebar_use_gradient: true,
    content_bg_color: '#fff1f2',
    content_bg_gradient_start: '#fff1f2',
    content_bg_gradient_end: '#ffe4e6',
    content_use_gradient: true
  },
  {
    name: 'Teal Modern',
    value: 'teal_modern',
    description: 'Segar & modern',
    icon: '💎',
    primary: '#14B8A6',
    secondary: '#2DD4BF',
    sidebar_color: '#134e4a',
    sidebar_gradient_start: '#134e4a',
    sidebar_gradient_end: '#115e59',
    sidebar_use_gradient: true,
    content_bg_color: '#f0fdfa',
    content_bg_gradient_start: '#f0fdfa',
    content_bg_gradient_end: '#ccfbf1',
    content_use_gradient: true
  }
];

// Theme presets (for individual primary/secondary color)
const THEME_PRESETS = [
  {
    name: 'Custom',
    value: 'custom',
    primary: '#4F46E5',
    secondary: '#10B981',
    description: 'Pilih warna sendiri'
  },
  {
    name: 'Blue Ocean',
    value: 'blue_ocean',
    primary: '#0EA5E9',
    secondary: '#06B6D4',
    description: 'Biru laut yang segar'
  },
  {
    name: 'Forest Green',
    value: 'forest_green',
    primary: '#059669',
    secondary: '#10B981',
    description: 'Hijau hutan yang natural'
  },
  {
    name: 'Sunset Orange',
    value: 'sunset_orange',
    primary: '#F97316',
    secondary: '#FB923C',
    description: 'Orange hangat seperti matahari terbenam'
  },
  {
    name: 'Royal Purple',
    value: 'royal_purple',
    primary: '#9333EA',
    secondary: '#C084FC',
    description: 'Ungu elegan dan mewah'
  },
  {
    name: 'Midnight Blue',
    value: 'midnight_blue',
    primary: '#1E3A8A',
    secondary: '#3B82F6',
    description: 'Biru gelap profesional'
  },
  {
    name: 'Rose Pink',
    value: 'rose_pink',
    primary: '#E11D48',
    secondary: '#F43F5E',
    description: 'Pink cerah dan energik'
  }
];

// Sidebar color presets
const SIDEBAR_PRESETS = [
  {
    name: 'Dark Slate',
    value: 'dark_slate',
    color: '#1e293b',
    gradient_start: '#1e293b',
    gradient_end: '#334155',
    description: 'Abu-abu gelap klasik'
  },
  {
    name: 'Midnight Navy',
    value: 'midnight_navy',
    color: '#0f172a',
    gradient_start: '#0f172a',
    gradient_end: '#1e3a5f',
    description: 'Biru navy malam'
  },
  {
    name: 'Deep Purple',
    value: 'deep_purple',
    color: '#2e1065',
    gradient_start: '#2e1065',
    gradient_end: '#4c1d95',
    description: 'Ungu gelap elegan'
  },
  {
    name: 'Dark Green',
    value: 'dark_green',
    color: '#14532d',
    gradient_start: '#14532d',
    gradient_end: '#166534',
    description: 'Hijau tua natural'
  },
  {
    name: 'Charcoal Black',
    value: 'charcoal_black',
    color: '#171717',
    gradient_start: '#171717',
    gradient_end: '#262626',
    description: 'Hitam profesional'
  },
  {
    name: 'Ocean Blue',
    value: 'ocean_blue',
    color: '#0c4a6e',
    gradient_start: '#0c4a6e',
    gradient_end: '#0369a1',
    description: 'Biru laut dalam'
  },
  {
    name: 'Wine Red',
    value: 'wine_red',
    color: '#450a0a',
    gradient_start: '#450a0a',
    gradient_end: '#7f1d1d',
    description: 'Merah maroon'
  },
  {
    name: 'Teal Dark',
    value: 'teal_dark',
    color: '#134e4a',
    gradient_start: '#134e4a',
    gradient_end: '#115e59',
    description: 'Teal gelap modern'
  }
];

// Content/Dashboard background presets
const CONTENT_BG_PRESETS = [
  {
    name: 'Light Gray',
    value: 'light_gray',
    color: '#f8fafc',
    gradient_start: '#f8fafc',
    gradient_end: '#e2e8f0',
    description: 'Abu-abu terang default'
  },
  {
    name: 'Pure White',
    value: 'pure_white',
    color: '#ffffff',
    gradient_start: '#ffffff',
    gradient_end: '#f1f5f9',
    description: 'Putih bersih'
  },
  {
    name: 'Warm Cream',
    value: 'warm_cream',
    color: '#fefce8',
    gradient_start: '#fefce8',
    gradient_end: '#fef3c7',
    description: 'Krem hangat'
  },
  {
    name: 'Cool Blue',
    value: 'cool_blue',
    color: '#f0f9ff',
    gradient_start: '#f0f9ff',
    gradient_end: '#e0f2fe',
    description: 'Biru sejuk'
  },
  {
    name: 'Soft Green',
    value: 'soft_green',
    color: '#f0fdf4',
    gradient_start: '#f0fdf4',
    gradient_end: '#dcfce7',
    description: 'Hijau lembut'
  },
  {
    name: 'Lavender',
    value: 'lavender',
    color: '#faf5ff',
    gradient_start: '#faf5ff',
    gradient_end: '#f3e8ff',
    description: 'Ungu lavender'
  },
  {
    name: 'Rose Mist',
    value: 'rose_mist',
    color: '#fff1f2',
    gradient_start: '#fff1f2',
    gradient_end: '#ffe4e6',
    description: 'Pink lembut'
  },
  {
    name: 'Dark Mode',
    value: 'dark_mode',
    color: '#0f172a',
    gradient_start: '#0f172a',
    gradient_end: '#1e293b',
    description: 'Mode gelap'
  }
];

// CSS overlay theme defaults — sidebar & header backgrounds for tema overlay
// are locked by !important in dashboard-themes.css. To make the mini preview
// match the actual rendered look, we use these bawaan values instead of
// the user's editable branding for sidebar/header (content_bg remains editable).
const THEME_OVERLAY_DEFAULTS = {
  solarized: {
    sidebar: 'linear-gradient(180deg, #134e4a 0%, #064e3b 100%)',
    header: '#ffffff',
    headerText: '#134e4a',
    sidebarText: '#ffffff',
    body: 'linear-gradient(180deg, #fffbeb 0%, #fef3c7 60%, #fffbeb 100%)',
  },
  sunset: {
    sidebar: 'linear-gradient(180deg, #fb7185, #f97316)',
    header: 'linear-gradient(90deg, #fb923c, #ec4899)',
    headerText: '#ffffff',
    sidebarText: '#ffffff',
    body: 'linear-gradient(135deg, #fff1e6 0%, #ffe0e9 35%, #f3e8ff 100%)',
  },
  forest: {
    sidebar: 'linear-gradient(180deg, #064e3b, #0f766e)',
    header: 'linear-gradient(90deg, #047857, #059669)',
    headerText: '#ffffff',
    sidebarText: '#ffffff',
    body: 'linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 100%)',
  },
  'cyber-neon': {
    sidebar: 'linear-gradient(180deg, #0a1124, #060912)',
    header: 'linear-gradient(90deg, #0d1326, #1a0a2e)',
    headerText: '#cdd6f4',
    sidebarText: '#cdd6f4',
    body: 'radial-gradient(ellipse at top, #0d1326 0%, #060912 60%, #000 100%)',
  },
  'corp-navy': {
    sidebar: 'linear-gradient(180deg, #0f1e44, #1e3a8a)',
    header: 'linear-gradient(90deg, #1e3a8a, #1e40af)',
    headerText: '#ffffff',
    sidebarText: '#ffffff',
    body: 'linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%)',
  },
  amoled: {
    sidebar: '#050505',
    header: '#000000',
    headerText: '#e5e5e5',
    sidebarText: '#e5e5e5',
    body: '#000000',
  },
};

// Header/Navbar color presets
const HEADER_PRESETS = [
  {
    name: 'Orange Sunset',
    value: 'orange_sunset',
    color: '#f97316',
    gradient_start: '#f97316',
    gradient_end: '#fb923c',
    description: 'Orange default'
  },
  {
    name: 'Blue Sky',
    value: 'blue_sky',
    color: '#0ea5e9',
    gradient_start: '#0ea5e9',
    gradient_end: '#38bdf8',
    description: 'Biru langit'
  },
  {
    name: 'Green Forest',
    value: 'green_forest',
    color: '#10b981',
    gradient_start: '#10b981',
    gradient_end: '#34d399',
    description: 'Hijau segar'
  },
  {
    name: 'Purple Royal',
    value: 'purple_royal',
    color: '#8b5cf6',
    gradient_start: '#8b5cf6',
    gradient_end: '#a78bfa',
    description: 'Ungu royal'
  },
  {
    name: 'Pink Rose',
    value: 'pink_rose',
    color: '#ec4899',
    gradient_start: '#ec4899',
    gradient_end: '#f472b6',
    description: 'Pink cerah'
  },
  {
    name: 'Red Fire',
    value: 'red_fire',
    color: '#ef4444',
    gradient_start: '#ef4444',
    gradient_end: '#f87171',
    description: 'Merah api'
  },
  {
    name: 'Teal Ocean',
    value: 'teal_ocean',
    color: '#14b8a6',
    gradient_start: '#14b8a6',
    gradient_end: '#2dd4bf',
    description: 'Teal segar'
  },
  {
    name: 'Indigo Night',
    value: 'indigo_night',
    color: '#6366f1',
    gradient_start: '#6366f1',
    gradient_end: '#818cf8',
    description: 'Indigo malam'
  }
];


const WhiteLabelSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTheme, setEditingTheme] = useState(() => {
    try { return localStorage.getItem('rl_dashboard_theme') || 'glass'; } catch { return 'glass'; }
  });
  const EDIT_THEMES = [
    { id: 'glass', label: 'Glassmorphism (default)' },
    { id: 'paper-white', label: 'Paper White' },
    { id: 'light', label: 'Clean Minimalist' },
    { id: 'dark', label: 'Dark Premium' },
  ];
  const [uploading, setUploading] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('custom');
  const [selectedSidebarPreset, setSelectedSidebarPreset] = useState('dark_slate');
  const [selectedContentBgPreset, setSelectedContentBgPreset] = useState('light_gray');
  const [selectedHeaderPreset, setSelectedHeaderPreset] = useState('orange_sunset');
  const [selectedCompleteTheme, setSelectedCompleteTheme] = useState(null);
  const [branding, setBranding] = useState({
    logo_url: '',
    logo_portal_url: '',
    logo_invoice_url: '',
    logo_scale: 1.5,
    primary_color: '#4F46E5',
    secondary_color: '#10B981',
    company_name: '',
    website: '',
    tagline: '',
    phone: '',
    email: '',
    address: '',
    // Sidebar settings
    sidebar_color: '#1e293b',
    sidebar_gradient_start: '#1e293b',
    sidebar_gradient_end: '#334155',
    sidebar_use_gradient: false,
    sidebar_text_color: '#ffffff',
    // Content background settings
    content_bg_color: '#f8fafc',
    content_bg_gradient_start: '#f8fafc',
    content_bg_gradient_end: '#e2e8f0',
    content_use_gradient: true,
    // Header settings
    header_color: '#f97316',
    header_gradient_start: '#f97316',
    header_gradient_end: '#fb923c',
    header_use_gradient: true,
    // Text color settings
    text_color: '#1f2937',
    header_text_color: '#ffffff'
  });

  const [preview, setPreview] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  // Stores raw root-level color fields from initial fetch — kept stable to avoid theme bleed at save time
  const rootBaselineRef = React.useRef(null);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/tenant/branding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        const d = response.data.data;
        const currentTheme = (() => { try { return localStorage.getItem('rl_dashboard_theme') || 'glass'; } catch { return 'glass'; } })();
        const ov = (d.theme_overrides || {})[currentTheme] || {};
        // Capture baseline root color fields once — used at save time so per-theme edits do not bleed into root
        if (!rootBaselineRef.current) {
          rootBaselineRef.current = {
            // Logo fields — captured so per-theme upload does not pollute root
            logo_url: d.logo_url,
            logo_portal_url: d.logo_portal_url,
            logo_invoice_url: d.logo_invoice_url,
            logo_scale: d.logo_scale,
            primary_color: d.primary_color,
            secondary_color: d.secondary_color,
            sidebar_color: d.sidebar_color,
            sidebar_gradient_start: d.sidebar_gradient_start,
            sidebar_gradient_end: d.sidebar_gradient_end,
            sidebar_use_gradient: d.sidebar_use_gradient,
            header_color: d.header_color,
            header_gradient_start: d.header_gradient_start,
            header_gradient_end: d.header_gradient_end,
            header_use_gradient: d.header_use_gradient,
            content_bg_color: d.content_bg_color,
            content_bg_gradient_start: d.content_bg_gradient_start,
            content_bg_gradient_end: d.content_bg_gradient_end,
            content_use_gradient: d.content_use_gradient,
            text_color: d.text_color,
            primary_text_color: d.primary_text_color,
            sidebar_text_color: d.sidebar_text_color,
            header_text_color: d.header_text_color,
          };
        }
        // Per-theme override aware getter: theme_overrides[currentTheme][field] -> root field -> fallback
        const pick = (field, fallback) => {
          if (ov[field] !== undefined && ov[field] !== null && ov[field] !== '') return ov[field];
          if (d[field] !== undefined && d[field] !== null && d[field] !== '') return d[field];
          return fallback;
        };
        // For boolean *_use_gradient fields, undefined-in-override means inherit root, false stays false
        const pickBool = (field, fallback) => {
          if (ov[field] !== undefined && ov[field] !== null) return ov[field];
          if (d[field] !== undefined && d[field] !== null) return d[field];
          return fallback;
        };
        setBranding({
          logo_url: pick('logo_url', ''),
          logo_portal_url: pick('logo_portal_url', ''),
          logo_invoice_url: pick('logo_invoice_url', ''),
          logo_scale: pick('logo_scale', 1.5),
          company_name: d.company_name || '',
          website: d.website || '',
          tagline: d.tagline || '',
          phone: d.phone || '',
          email: d.email || '',
          address: d.address || '',
          // Per-theme aware fields (17 color/gradient fields)
          primary_color: pick('primary_color', '#4F46E5'),
          secondary_color: pick('secondary_color', '#10B981'),
          // Sidebar
          sidebar_color: pick('sidebar_color', '#1e293b'),
          sidebar_gradient_start: pick('sidebar_gradient_start', '#1e293b'),
          sidebar_gradient_end: pick('sidebar_gradient_end', '#334155'),
          sidebar_use_gradient: pickBool('sidebar_use_gradient', false),
          // Content background
          content_bg_color: pick('content_bg_color', '#f8fafc'),
          content_bg_gradient_start: pick('content_bg_gradient_start', '#f8fafc'),
          content_bg_gradient_end: pick('content_bg_gradient_end', '#e2e8f0'),
          content_use_gradient: pickBool('content_use_gradient', true),
          // Header
          header_color: pick('header_color', '#f97316'),
          header_gradient_start: pick('header_gradient_start', '#f97316'),
          header_gradient_end: pick('header_gradient_end', '#fb923c'),
          header_use_gradient: pickBool('header_use_gradient', true),
          // Text colors
          text_color: pick('text_color', '#1f2937'),
          primary_text_color: pick('primary_text_color', '#1f2937'),
          sidebar_text_color: pick('sidebar_text_color', '#ffffff'),
          header_text_color: pick('header_text_color', '#ffffff'),
          theme_overrides: d.theme_overrides || {}
        });
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
      toast.error('Gagal memuat branding');
    } finally {
      setLoading(false);
    }
  };

  const handlePresetChange = (presetValue) => {
    setSelectedPreset(presetValue);
    
    if (presetValue === 'custom') {
      // Keep current colors
      return;
    }
    
    const preset = THEME_PRESETS.find(p => p.value === presetValue);
    if (preset) {
      setBranding({
        ...branding,
        primary_color: preset.primary,
        secondary_color: preset.secondary
      });
      toast.success(`Theme "${preset.name}" dipilih!`);
    }
  };

  const handleHeaderPresetChange = (presetValue) => {
    setSelectedHeaderPreset(presetValue);
    setSelectedCompleteTheme(null);
    
    const preset = HEADER_PRESETS.find(p => p.value === presetValue);
    if (preset) {
      setBranding({
        ...branding,
        header_color: preset.color,
        header_gradient_start: preset.gradient_start,
        header_gradient_end: preset.gradient_end
      });
      toast.success(`Header "${preset.name}" dipilih!`);
    }
  };

  const handleSidebarPresetChange = (presetValue) => {
    setSelectedSidebarPreset(presetValue);
    setSelectedCompleteTheme(null); // Reset complete theme when individual changed
    
    const preset = SIDEBAR_PRESETS.find(p => p.value === presetValue);
    if (preset) {
      setBranding({
        ...branding,
        sidebar_color: preset.color,
        sidebar_gradient_start: preset.gradient_start,
        sidebar_gradient_end: preset.gradient_end
      });
      toast.success(`Sidebar "${preset.name}" dipilih!`);
    }
  };

  const handleContentBgPresetChange = (presetValue) => {
    setSelectedContentBgPreset(presetValue);
    setSelectedCompleteTheme(null); // Reset complete theme when individual changed
    
    const preset = CONTENT_BG_PRESETS.find(p => p.value === presetValue);
    if (preset) {
      setBranding({
        ...branding,
        content_bg_color: preset.color,
        content_bg_gradient_start: preset.gradient_start,
        content_bg_gradient_end: preset.gradient_end
      });
      toast.success(`Background "${preset.name}" dipilih!`);
    }
  };

  // Handler for complete theme preset - applies ALL colors at once
  const handleCompleteThemeChange = (themeValue) => {
    setSelectedCompleteTheme(themeValue);
    
    const theme = COMPLETE_THEME_PRESETS.find(t => t.value === themeValue);
    if (theme) {
      setBranding({
        ...branding,
        primary_color: theme.primary,
        secondary_color: theme.secondary,
        sidebar_color: theme.sidebar_color,
        sidebar_gradient_start: theme.sidebar_gradient_start,
        sidebar_gradient_end: theme.sidebar_gradient_end,
        sidebar_use_gradient: theme.sidebar_use_gradient,
        content_bg_color: theme.content_bg_color,
        content_bg_gradient_start: theme.content_bg_gradient_start,
        content_bg_gradient_end: theme.content_bg_gradient_end,
        content_use_gradient: theme.content_use_gradient
      });
      toast.success(`🎨 Tema "${theme.name}" diterapkan!`, {
        description: 'Semua warna sudah diubah sekaligus'
      });
    }
  };

  const handleResetTheme = async () => {
    if (!window.confirm(`Reset tema "${editingTheme}" ke bawaan? Override warna untuk theme ini akan dihapus permanen.`)) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const existingOverrides = { ...(branding.theme_overrides || {}) };
      delete existingOverrides[editingTheme];
      // Restore root color fields to baseline so reset does not pollute root
      const THEME_FIELDS = [
        'primary_color','secondary_color',
        'primary_text_color','sidebar_text_color','header_text_color',
        'sidebar_color','sidebar_gradient_start','sidebar_gradient_end','sidebar_use_gradient',
        'header_color','header_gradient_start','header_gradient_end','header_use_gradient',
        'content_bg_color','content_bg_gradient_start','content_bg_gradient_end','content_use_gradient',
        'text_color',
        'logo_url','logo_portal_url','logo_invoice_url','logo_scale',
      ];
      const baseline = rootBaselineRef.current || {};
      const payload = { ...branding };
      THEME_FIELDS.forEach((f) => { if (baseline[f] !== undefined) payload[f] = baseline[f]; });
      payload.theme_overrides = existingOverrides;
      await axios.put(`${API_URL}/api/v2/tenant/branding`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Tema "${editingTheme}" berhasil di-reset ke bawaan`);
      setTimeout(() => window.location.reload(), 700);
    } catch (err) {
      console.error('Reset theme error:', err);
      toast.error('Gagal reset tema: ' + (err?.response?.data?.detail || err.message));
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 2MB');
      return;
    }

    try {
      setUploading(true);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setLogoPreview(base64String);
        setBranding({...branding, logo_url: base64String});
        toast.success('Logo berhasil diupload! Jangan lupa klik Save.');
      };
      reader.readAsDataURL(file);
      
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Gagal upload logo');
    } finally {
      setUploading(false);
    }
  };


  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      // Save ALL color-related fields to theme_overrides[editingTheme] ONLY.
      // Root-level color fields are kept frozen at the baseline captured on first fetch to prevent
      // the currently-edited theme from bleeding its colors back into the global root (which would
      // make other themes inherit them as a fallback).
      const THEME_FIELDS = [
        'primary_color', 'secondary_color',
        'primary_text_color', 'sidebar_text_color', 'header_text_color',
        'sidebar_color', 'sidebar_gradient_start', 'sidebar_gradient_end', 'sidebar_use_gradient',
        'header_color', 'header_gradient_start', 'header_gradient_end', 'header_use_gradient',
        'content_bg_color', 'content_bg_gradient_start', 'content_bg_gradient_end', 'content_use_gradient',
        'text_color',
        // Logo per-theme — allows different logo for each theme (e.g. dark logo for Paper White)
        'logo_url', 'logo_portal_url', 'logo_invoice_url', 'logo_scale',
      ];
      const existingOverrides = branding.theme_overrides || {};
      const baseline = rootBaselineRef.current || {};
      const themeOverride = { ...(existingOverrides[editingTheme] || {}) };
      // DIFF-AWARE OVERRIDE: only store fields that ACTUALLY differ from baseline (root).
      // This prevents the act of "upload logo" or "save" from accidentally overriding
      // colors that the user did not explicitly change for this theme.
      THEME_FIELDS.forEach((f) => {
        const cur = branding[f];
        const base = baseline[f];
        if (cur !== undefined && cur !== null && cur !== '' && cur !== base) {
          themeOverride[f] = cur;
        } else {
          // Equal to baseline (or empty) → remove from override so this field
          // inherits root (and per-theme CSS defaults apply).
          delete themeOverride[f];
        }
      });
      const themeOverrides = { ...existingOverrides, [editingTheme]: themeOverride };
      // Build payload: start with current branding state, then OVERWRITE the THEME_FIELDS at root
      // with the immutable baseline so editing a theme does not pollute root.
      const payload = { ...branding };
      THEME_FIELDS.forEach((f) => {
        if (baseline[f] !== undefined) payload[f] = baseline[f];
      });
      // FIX 2026-10-04 (Delta #26): Logo fields ALWAYS sync to root branding.
      // Portal pelanggan, halaman login, dan invoice cetak tidak punya konteks tema dashboard
      // — mereka membaca field ROOT (logo_url/logo_portal_url/logo_invoice_url). Jika logo
      // hanya tersimpan di theme_overrides, portal tampak "hardcode" (tidak pernah berubah).
      // Per-theme override tetap berlaku di dashboard via _applyThemeOverrides.
      // Logo yang sedang di-upload/dilihat pada tema aktif menjadi sumber kebenaran root.
      // Jika tema aktif punya override logo, root ikut override tsb; jika tidak, pakai state form.
      payload.logo_url = themeOverride.logo_url || branding.logo_url;
      payload.logo_portal_url = themeOverride.logo_portal_url || themeOverride.logo_url || branding.logo_portal_url;
      payload.logo_invoice_url = themeOverride.logo_invoice_url || themeOverride.logo_url || branding.logo_invoice_url;
      payload.logo_scale = themeOverride.logo_scale || branding.logo_scale;
      payload.theme_overrides = themeOverrides;
      const response = await axios.put(
        `${API_URL}/api/v2/tenant/branding`,
        payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Branding berhasil diperbarui!');
        
        // Reload page to apply new branding
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error('Error updating branding:', error);
      toast.error(error.response?.data?.detail || 'Gagal memperbarui branding');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-gray-500">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600">
          <CardTitle className="text-white flex items-center gap-2">
            <Palette className="w-5 h-5" />
            White Label Settings
          </CardTitle>
          <p className="text-purple-100 text-sm mt-1">
            Customize branding untuk perusahaan Anda
          </p>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">

            {/* STICKY EDITING-THEME BANNER — clear indicator of which theme will be saved */}
            <div className="sticky top-0 z-30 -mx-6 px-6 py-3 bg-amber-50 border-y-2 border-amber-300 shadow-sm">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                    <Palette className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-800">Anda sedang mengedit tema</p>
                    <p className="text-sm font-bold text-amber-900 leading-tight truncate">
                      {(EDIT_THEMES.find(t => t.id === editingTheme)?.label) || editingTheme}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <label className="text-xs font-semibold text-amber-800 hidden sm:inline">Ganti:</label>
                  <select
                    value={editingTheme}
                    onChange={(e) => {
                      const t = e.target.value;
                      setEditingTheme(t);
                      const ov = (branding.theme_overrides || {})[t] || {};
                      const baseline = rootBaselineRef.current || {};
                      const F = [
                        'primary_color','secondary_color',
                        'primary_text_color','sidebar_text_color','header_text_color',
                        'sidebar_color','sidebar_gradient_start','sidebar_gradient_end','sidebar_use_gradient',
                        'header_color','header_gradient_start','header_gradient_end','header_use_gradient',
                        'content_bg_color','content_bg_gradient_start','content_bg_gradient_end','content_use_gradient',
                        'text_color',
                        'logo_url','logo_portal_url','logo_invoice_url','logo_scale',
                      ];
                      const next = { ...branding };
                      F.forEach((f) => {
                        let v;
                        if (ov[f] !== undefined && ov[f] !== null && ov[f] !== '') v = ov[f];
                        else if (baseline[f] !== undefined && baseline[f] !== null && baseline[f] !== '') v = baseline[f];
                        else v = branding[f];
                        next[f] = v;
                      });
                      setBranding(next);
                      try { localStorage.setItem('rl_dashboard_theme', t); window.dispatchEvent(new Event('rl-theme-change')); } catch {}
                      toast.info(`Mengedit tema: ${EDIT_THEMES.find(x=>x.id===t)?.label || t}`);
                    }}
                    className="text-xs font-semibold px-2 py-1.5 rounded-md border border-amber-400 bg-white text-amber-900 cursor-pointer focus:ring-2 focus:ring-amber-400"
                    data-testid="sticky-editing-theme"
                  >
                    {EDIT_THEMES.map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* COMPLETE THEME PRESET - NEW SECTION */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-5 rounded-xl border border-indigo-200">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-600" />
                ✨ Tema Cepat (1 Klik = Semua Warna Berubah)
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Pilih tema dan semua warna (sidebar, background, primary, secondary) akan berubah sekaligus!
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {COMPLETE_THEME_PRESETS.map(theme => (
                  <button
                    key={theme.value}
                    onClick={() => handleCompleteThemeChange(theme.value)}
                    className={`p-3 rounded-xl border-2 transition-all hover:scale-105 hover:shadow-lg ${
                      selectedCompleteTheme === theme.value
                        ? 'border-indigo-500 shadow-lg ring-2 ring-indigo-200'
                        : 'border-gray-200 hover:border-indigo-300'
                    }`}
                  >
                    {/* Theme Preview Mini */}
                    <div className="flex rounded-lg overflow-hidden h-12 mb-2 border border-gray-300">
                      {/* Sidebar mini */}
                      <div 
                        className="w-4"
                        style={{ 
                          background: theme.sidebar_use_gradient 
                            ? `linear-gradient(180deg, ${theme.sidebar_gradient_start} 0%, ${theme.sidebar_gradient_end} 100%)`
                            : theme.sidebar_color
                        }}
                      />
                      {/* Content mini */}
                      <div 
                        className="flex-1 flex items-center justify-center"
                        style={{ 
                          background: theme.content_use_gradient 
                            ? `linear-gradient(135deg, ${theme.content_bg_gradient_start} 0%, ${theme.content_bg_gradient_end} 100%)`
                            : theme.content_bg_color
                        }}
                      >
                        {/* Primary color dot */}
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: theme.primary }}
                        />
                      </div>
                    </div>
                    <p className="text-xs font-semibold text-gray-800 flex items-center gap-1">
                      <span>{theme.icon}</span> {theme.name}
                    </p>
                    <p className="text-[10px] text-gray-500 truncate">{theme.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs text-gray-500 italic mb-4">
                💡 Atau customize warna secara individual di bawah ini:
              </p>
            </div>

            {/* Logo Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Logo Perusahaan
              </label>
              <div className="flex items-center gap-4">
                {(logoPreview || branding.logo_url) && (
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <div className="w-32 h-20 border-2 border-slate-700 rounded-lg p-2 flex items-center justify-center bg-slate-900" title="Preview di background GELAP (sidebar/portal dark)">
                        <img src={logoPreview || branding.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                      </div>
                      <div className="w-32 h-20 border-2 border-gray-200 rounded-lg p-2 flex items-center justify-center bg-white" title="Preview di kertas PUTIH (invoice)">
                        <img src={logoPreview || branding.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                      </div>
                    </div>
                    <div className="flex gap-2 text-[10px] text-gray-500 dark:text-gray-400">
                      <div className="w-32 text-center">Bg Gelap</div>
                      <div className="w-32 text-center">Kertas Putih</div>
                    </div>
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <label
                    htmlFor="logo-upload"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition"
                  >
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Uploading...' : 'Upload Logo'}
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    Upload file gambar (PNG/JPG/SVG). Maksimal 2MB. Rekomendasi: 200x200px, background transparan.
                  </p>
                </div>
              </div>
              
              {/* Logo Scale Slider */}
              {branding.logo_url && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    🔍 Ukuran Logo: {((branding.logo_scale || 1.5) * 100).toFixed(0)}%
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="2.5"
                    step="0.1"
                    value={branding.logo_scale || 1.5}
                    onChange={(e) => handleChange('logo_scale', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                  />
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>50%</span>
                    <span>100%</span>
                    <span>150%</span>
                    <span>200%</span>
                    <span>250%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Geser slider untuk memperbesar/mengecilkan logo. Berguna jika logo memiliki banyak padding transparan.
                  </p>
                </div>
              )}
            </div>

            {/* Logo Portal Pelanggan - Dark version for light background */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-blue-600" />
                Logo Portal Pelanggan (Versi Hitam/Gelap)
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Upload logo versi gelap/hitam untuk Portal Pelanggan yang memiliki background terang.
              </p>
              <div className="flex items-start gap-4">
                {branding.logo_portal_url ? (
                  <div className="relative flex flex-col gap-2">
                    <div className="flex gap-2">
                      <div className="w-28 h-20 border-2 border-slate-700 rounded-lg p-2 flex items-center justify-center bg-slate-900" title="Preview di background GELAP (portal login dark)">
                        <img src={branding.logo_portal_url} alt="Logo Portal" className="max-w-full max-h-full object-contain" />
                      </div>
                      <div className="w-28 h-20 border-2 border-blue-200 rounded-lg p-2 flex items-center justify-center bg-white" title="Preview di background PUTIH">
                        <img src={branding.logo_portal_url} alt="Logo Portal" className="max-w-full max-h-full object-contain" />
                      </div>
                    </div>
                    <div className="flex gap-2 text-[10px] text-gray-500 dark:text-gray-400">
                      <div className="w-28 text-center">Bg Gelap ← target</div>
                      <div className="w-28 text-center">Kertas Putih</div>
                    </div>
                    <button type="button" onClick={() => handleChange('logo_portal_url', '')} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600">×</button>
                  </div>
                ) : (
                  <div className="w-24 h-24 border-2 border-dashed border-blue-300 rounded-lg flex items-center justify-center bg-white">
                    <ImageIcon className="w-8 h-8 text-blue-300" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    id="logo-portal-upload"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (!file.type.startsWith('image/')) {
                        toast.error('File harus berupa gambar');
                        return;
                      }
                      if (file.size > 2 * 1024 * 1024) {
                        toast.error('Ukuran file maksimal 2MB');
                        return;
                      }
                      try {
                        setUploading(true);
                        // Convert to base64 (same as main logo)
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const base64String = reader.result;
                          setBranding({...branding, logo_portal_url: base64String});
                          toast.success('Logo Portal berhasil diupload! Jangan lupa klik Save.');
                          setUploading(false);
                        };
                        reader.readAsDataURL(file);
                      } catch (error) {
                        console.error('Error uploading logo portal:', error);
                        toast.error('Gagal upload logo portal');
                        setUploading(false);
                      }
                    }}
                    className="hidden"
                  />
                  <label
                    htmlFor="logo-portal-upload"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Logo Portal
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    Upload logo berwarna hitam/gelap untuk tampilan di Portal Pelanggan.
                  </p>
                </div>
              </div>
            </div>

            {/* Logo Invoice - For printing invoices */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-green-600" />
                Logo Invoice (Khusus Cetak)
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Upload logo versi gelap/hitam yang akan tampil di invoice cetak (untuk kertas putih).
              </p>
              <div className="flex items-start gap-4">
                {branding.logo_invoice_url ? (
                  <div className="relative flex flex-col gap-2">
                    <div className="flex gap-2">
                      <div className="w-28 h-20 border-2 border-slate-700 rounded-lg p-2 flex items-center justify-center bg-slate-900" title="Preview di background GELAP">
                        <img src={branding.logo_invoice_url} alt="Logo Invoice" className="max-w-full max-h-full object-contain" />
                      </div>
                      <div className="w-28 h-20 border-2 border-green-300 rounded-lg p-2 flex items-center justify-center bg-white" title="Preview di kertas PUTIH (invoice cetak)">
                        <img src={branding.logo_invoice_url} alt="Logo Invoice" className="max-w-full max-h-full object-contain" />
                      </div>
                    </div>
                    <div className="flex gap-2 text-[10px] text-gray-500 dark:text-gray-400">
                      <div className="w-28 text-center">Bg Gelap</div>
                      <div className="w-28 text-center">Kertas Putih ← target</div>
                    </div>
                    <button type="button" onClick={() => handleChange('logo_invoice_url', '')} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600">×</button>
                  </div>
                ) : (
                  <div className="w-24 h-24 border-2 border-dashed border-green-300 rounded-lg flex items-center justify-center bg-white">
                    <ImageIcon className="w-8 h-8 text-green-300" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    id="logo-invoice-upload"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (!file.type.startsWith('image/')) {
                        toast.error('File harus berupa gambar');
                        return;
                      }
                      if (file.size > 2 * 1024 * 1024) {
                        toast.error('Ukuran file maksimal 2MB');
                        return;
                      }
                      try {
                        setUploading(true);
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const base64String = reader.result;
                          setBranding({...branding, logo_invoice_url: base64String});
                          toast.success('Logo Invoice berhasil diupload! Jangan lupa klik Save.');
                          setUploading(false);
                        };
                        reader.readAsDataURL(file);
                      } catch (error) {
                        console.error('Error uploading logo invoice:', error);
                        toast.error('Gagal upload logo invoice');
                        setUploading(false);
                      }
                    }}
                    className="hidden"
                  />
                  <label
                    htmlFor="logo-invoice-upload"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer transition"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Logo Invoice
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    Logo ini akan digunakan khusus saat mencetak invoice (background putih).
                  </p>
                </div>
              </div>
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Nama Perusahaan
              </label>
              <Input
                value={branding.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="PT. ISP Indonesia"
              />
            </div>

            {/* Theme Preset Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Pilih Theme Preset
              </label>
              <select
                value={selectedPreset}
                onChange={(e) => handlePresetChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {THEME_PRESETS.map(preset => (
                  <option key={preset.value} value={preset.value}>
                    {preset.name} - {preset.description}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Pilih preset untuk apply warna otomatis, atau pilih &quot;Custom&quot; untuk set manual
              </p>
            </div>

            {/* Theme Preview Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {THEME_PRESETS.filter(p => p.value !== 'custom').map(preset => (
                <button
                  key={preset.value}
                  onClick={() => handlePresetChange(preset.value)}
                  className={`p-3 rounded-lg border-2 transition-all hover:scale-105 ${
                    selectedPreset === preset.value
                      ? 'border-indigo-500 shadow-md'
                      : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                  }`}
                >
                  <div className="flex gap-1 mb-2">
                    <div 
                      className="w-8 h-8 rounded"
                      style={{ backgroundColor: preset.primary }}
                    />
                    <div 
                      className="w-8 h-8 rounded"
                      style={{ backgroundColor: preset.secondary }}
                    />
                  </div>
                  <p className="text-xs font-medium text-white dark:text-white">{preset.name}</p>
                </button>
              ))}
            </div>

            {/* Colors */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Warna Utama (Primary)
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={branding.primary_color}
                    onChange={(e) => {
                      handleChange('primary_color', e.target.value);
                      setSelectedPreset('custom'); // Switch to custom when manually changed
                    }}
                    className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                  />
                  <Input
                    value={branding.primary_color}
                    onChange={(e) => {
                      handleChange('primary_color', e.target.value);
                      setSelectedPreset('custom');
                    }}
                    placeholder="#4F46E5"
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Warna Sekunder (Secondary)
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={branding.secondary_color}
                    onChange={(e) => {
                      handleChange('secondary_color', e.target.value);
                      setSelectedPreset('custom');
                    }}
                    className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                  />
                  <Input
                    value={branding.secondary_color}
                    onChange={(e) => {
                      handleChange('secondary_color', e.target.value);
                      setSelectedPreset('custom');
                    }}
                    placeholder="#10B981"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            {/* Header/Navbar Settings Section */}
            <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5 text-orange-600" />
                Warna Header / Navbar (Bar Atas)
              </h3>

              {/* Header Type Toggle */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Tipe Warna Header
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="header_type"
                      checked={!branding.header_use_gradient}
                      onChange={() => handleChange('header_use_gradient', false)}
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Warna Solid</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="header_type"
                      checked={branding.header_use_gradient}
                      onChange={() => handleChange('header_use_gradient', true)}
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Gradient (2 Warna)</span>
                  </label>
                </div>
              </div>

              {/* Header Preset Cards */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Pilih Preset Warna Header
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {HEADER_PRESETS.map(preset => (
                    <button
                      key={preset.value}
                      onClick={() => handleHeaderPresetChange(preset.value)}
                      className={`p-3 rounded-lg border-2 transition-all hover:scale-105 ${
                        selectedHeaderPreset === preset.value
                          ? 'border-orange-500 shadow-md'
                          : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                      }`}
                    >
                      <div 
                        className="w-full h-8 rounded mb-2"
                        style={{ 
                          background: `linear-gradient(90deg, ${preset.gradient_start} 0%, ${preset.gradient_end} 100%)`
                        }}
                      />
                      <p className="text-xs font-medium text-gray-100 dark:text-gray-100 truncate">{preset.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Header Color Inputs */}
              {!branding.header_use_gradient ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Warna Header (Solid)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={branding.header_color}
                      onChange={(e) => handleChange('header_color', e.target.value)}
                      className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                    />
                    <Input
                      value={branding.header_color}
                      onChange={(e) => handleChange('header_color', e.target.value)}
                      placeholder="#f97316"
                      className="flex-1"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Warna Kiri
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={branding.header_gradient_start}
                        onChange={(e) => handleChange('header_gradient_start', e.target.value)}
                        className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                      />
                      <Input
                        value={branding.header_gradient_start}
                        onChange={(e) => handleChange('header_gradient_start', e.target.value)}
                        placeholder="#f97316"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Warna Kanan
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={branding.header_gradient_end}
                        onChange={(e) => handleChange('header_gradient_end', e.target.value)}
                        className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                      />
                      <Input
                        value={branding.header_gradient_end}
                        onChange={(e) => handleChange('header_gradient_end', e.target.value)}
                        placeholder="#fb923c"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Header Preview */}
              <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Preview Header:</p>
                <div 
                  className="w-full h-12 rounded-lg flex items-center justify-between px-4"
                  style={
                    branding.header_use_gradient 
                      ? { background: `linear-gradient(90deg, ${branding.header_gradient_start} 0%, ${branding.header_gradient_end} 100%)` }
                      : { backgroundColor: branding.header_color }
                  }
                >
                  <p className="text-white font-semibold text-sm">📊 Dashboard</p>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-white/20 rounded"></div>
                    <div className="w-6 h-6 bg-white/20 rounded"></div>
                    <div className="w-6 h-6 bg-white/30 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar Settings Section */}
            <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <PanelLeft className="w-5 h-5 text-indigo-600" />
                Warna Sidebar / Menu Navigasi
              </h3>

              {/* Sidebar Type Toggle */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Tipe Warna Sidebar
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sidebar_type"
                      checked={!branding.sidebar_use_gradient}
                      onChange={() => handleChange('sidebar_use_gradient', false)}
                      className="w-4 h-4 text-indigo-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Warna Solid</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sidebar_type"
                      checked={branding.sidebar_use_gradient}
                      onChange={() => handleChange('sidebar_use_gradient', true)}
                      className="w-4 h-4 text-indigo-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Gradient (2 Warna)</span>
                  </label>
                </div>
              </div>

              {/* Sidebar Preset Cards */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Pilih Preset Warna Sidebar
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {SIDEBAR_PRESETS.map(preset => (
                    <button
                      key={preset.value}
                      onClick={() => handleSidebarPresetChange(preset.value)}
                      className={`p-3 rounded-lg border-2 transition-all hover:scale-105 ${
                        selectedSidebarPreset === preset.value
                          ? 'border-indigo-500 shadow-md'
                          : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                      }`}
                    >
                      <div 
                        className="w-full h-10 rounded mb-2"
                        style={{ 
                          background: `linear-gradient(180deg, ${preset.gradient_start} 0%, ${preset.gradient_end} 100%)`
                        }}
                      />
                      <p className="text-xs font-medium text-gray-100 dark:text-gray-100 truncate">{preset.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Sidebar Color Inputs */}
              {!branding.sidebar_use_gradient ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Warna Sidebar (Solid)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={branding.sidebar_color}
                      onChange={(e) => handleChange('sidebar_color', e.target.value)}
                      className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                    />
                    <Input
                      value={branding.sidebar_color}
                      onChange={(e) => handleChange('sidebar_color', e.target.value)}
                      placeholder="#1e293b"
                      className="flex-1"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Warna Awal (Atas)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={branding.sidebar_gradient_start}
                        onChange={(e) => handleChange('sidebar_gradient_start', e.target.value)}
                        className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                      />
                      <Input
                        value={branding.sidebar_gradient_start}
                        onChange={(e) => handleChange('sidebar_gradient_start', e.target.value)}
                        placeholder="#1e293b"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Warna Akhir (Bawah)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={branding.sidebar_gradient_end}
                        onChange={(e) => handleChange('sidebar_gradient_end', e.target.value)}
                        className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                      />
                      <Input
                        value={branding.sidebar_gradient_end}
                        onChange={(e) => handleChange('sidebar_gradient_end', e.target.value)}
                        placeholder="#334155"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Sidebar Preview */}
              <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Preview Sidebar:</p>
                <div 
                  className="w-full h-24 rounded-lg flex items-center justify-center"
                  style={
                    branding.sidebar_use_gradient 
                      ? { background: `linear-gradient(180deg, ${branding.sidebar_gradient_start} 0%, ${branding.sidebar_gradient_end} 100%)` }
                      : { backgroundColor: branding.sidebar_color }
                  }
                >
                  <div className="text-white text-center">
                    <p className="text-sm font-medium">📋 Menu</p>
                    <p className="text-xs opacity-70">Dashboard, Pelanggan, dll</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Dashboard/Content Background Section */}
            <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5 text-indigo-600" />
                Warna Background Dashboard
              </h3>

              {/* Content BG Type Toggle */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Tipe Background
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="content_bg_type"
                      checked={!branding.content_use_gradient}
                      onChange={() => handleChange('content_use_gradient', false)}
                      className="w-4 h-4 text-indigo-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Warna Solid</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="content_bg_type"
                      checked={branding.content_use_gradient}
                      onChange={() => handleChange('content_use_gradient', true)}
                      className="w-4 h-4 text-indigo-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Gradient (2 Warna)</span>
                  </label>
                </div>
              </div>

              {/* Content BG Preset Cards */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Pilih Preset Background
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {CONTENT_BG_PRESETS.map(preset => (
                    <button
                      key={preset.value}
                      onClick={() => handleContentBgPresetChange(preset.value)}
                      className={`p-3 rounded-lg border-2 transition-all hover:scale-105 ${
                        selectedContentBgPreset === preset.value
                          ? 'border-indigo-500 shadow-md'
                          : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                      }`}
                    >
                      <div 
                        className="w-full h-10 rounded mb-2 border border-gray-200"
                        style={{ 
                          background: `linear-gradient(135deg, ${preset.gradient_start} 0%, ${preset.gradient_end} 100%)`
                        }}
                      />
                      <p className="text-xs font-medium text-gray-100 dark:text-gray-100 truncate">{preset.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Content BG Color Inputs */}
              {!branding.content_use_gradient ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Warna Background (Solid)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={branding.content_bg_color}
                      onChange={(e) => handleChange('content_bg_color', e.target.value)}
                      className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                    />
                    <Input
                      value={branding.content_bg_color}
                      onChange={(e) => handleChange('content_bg_color', e.target.value)}
                      placeholder="#f8fafc"
                      className="flex-1"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Warna Awal
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={branding.content_bg_gradient_start}
                        onChange={(e) => handleChange('content_bg_gradient_start', e.target.value)}
                        className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                      />
                      <Input
                        value={branding.content_bg_gradient_start}
                        onChange={(e) => handleChange('content_bg_gradient_start', e.target.value)}
                        placeholder="#f8fafc"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Warna Akhir
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={branding.content_bg_gradient_end}
                        onChange={(e) => handleChange('content_bg_gradient_end', e.target.value)}
                        className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                      />
                      <Input
                        value={branding.content_bg_gradient_end}
                        onChange={(e) => handleChange('content_bg_gradient_end', e.target.value)}
                        placeholder="#e2e8f0"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Mini Dashboard Live Preview — auto-updates as user changes any color */}
              <div className="mt-4 p-4 rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900" data-testid="mini-preview-wrapper">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5" /> Live Preview Dashboard
                  </p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-medium">
                    Realtime
                  </span>
                </div>
                <div
                  className="rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 shadow-md flex"
                  style={{ height: 220 }}
                  data-testid="mini-preview-dashboard"
                >
                  {/* Mini Sidebar — use bawaan tema overlay if applicable, else editable branding */}
                  <div
                    className="w-20 flex flex-col py-2.5 px-1.5 gap-1.5"
                    style={(() => {
                      const ov = THEME_OVERLAY_DEFAULTS[editingTheme];
                      if (ov) return { background: ov.sidebar, color: branding.sidebar_text_color || ov.sidebarText };
                      return branding.sidebar_use_gradient
                        ? { background: `linear-gradient(180deg, ${branding.sidebar_gradient_start} 0%, ${branding.sidebar_gradient_end} 100%)`, color: branding.sidebar_text_color || '#ffffff' }
                        : { backgroundColor: branding.sidebar_color, color: branding.sidebar_text_color || '#ffffff' };
                    })()}
                  >
                    <div className="text-[8px] font-bold opacity-90 px-1 mb-1 truncate">LOGO</div>
                    {['Dashboard', 'Pelanggan', 'Voucher', 'Cabang', 'Akuntansi'].map((m, i) => (
                      <div
                        key={m}
                        className="text-[8px] py-1 px-1.5 rounded truncate"
                        style={
                          i === 0
                            ? { background: `linear-gradient(90deg, ${branding.primary_color || '#4F46E5'}, ${branding.secondary_color || '#10B981'})`, color: '#fff' }
                            : { opacity: 0.85 }
                        }
                      >
                        {m}
                      </div>
                    ))}
                  </div>

                  {/* Right column: header + content */}
                  <div className="flex-1 flex flex-col min-w-0">
                    {/* Mini Header — bawaan tema overlay or editable branding */}
                    <div
                      className="h-7 flex items-center justify-between px-2.5"
                      style={(() => {
                        const ov = THEME_OVERLAY_DEFAULTS[editingTheme];
                        if (ov) return { background: ov.header, color: branding.header_text_color || ov.headerText };
                        return branding.header_use_gradient
                          ? { background: `linear-gradient(90deg, ${branding.header_gradient_start} 0%, ${branding.header_gradient_end} 100%)`, color: branding.header_text_color || '#ffffff' }
                          : { backgroundColor: branding.header_color, color: branding.header_text_color || '#ffffff' };
                      })()}
                    >
                      <span className="text-[9px] font-semibold">Dashboard</span>
                      <span className="text-[8px] opacity-80">KPO DEPOK</span>
                    </div>

                    {/* Mini Content */}
                    <div
                      className="flex-1 p-2 overflow-hidden"
                      style={
                        branding.content_use_gradient
                          ? { background: `linear-gradient(135deg, ${branding.content_bg_gradient_start} 0%, ${branding.content_bg_gradient_end} 100%)`, color: branding.primary_text_color || '#1f2937' }
                          : { backgroundColor: branding.content_bg_color, color: branding.primary_text_color || '#1f2937' }
                      }
                    >
                      <div className="grid grid-cols-3 gap-1.5 mb-1.5">
                        {[
                          { label: 'Pelanggan', value: '1.8K', tone: branding.primary_color || '#4F46E5' },
                          { label: 'Online', value: '7', tone: branding.secondary_color || '#10B981' },
                          { label: 'Belum Bayar', value: '1.2K', tone: '#f59e0b' },
                        ].map((kpi) => (
                          <div key={kpi.label} className="bg-white/85 dark:bg-slate-900/70 rounded p-1 backdrop-blur-sm">
                            <div className="text-[7px] uppercase tracking-wider opacity-70 truncate">{kpi.label}</div>
                            <div className="text-[10px] font-bold" style={{ color: kpi.tone }}>{kpi.value}</div>
                          </div>
                        ))}
                      </div>
                      <div className="bg-white/85 dark:bg-slate-900/70 rounded p-1.5 backdrop-blur-sm">
                        <div className="text-[7px] uppercase tracking-wider opacity-70 mb-1">Tren Pendapatan</div>
                        <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: '68%',
                              background: `linear-gradient(90deg, ${branding.primary_color || '#4F46E5'}, ${branding.secondary_color || '#10B981'})`,
                            }}
                          />
                        </div>
                        <div className="flex justify-between mt-1 text-[7px] opacity-60">
                          <span>Jan</span><span>Apr</span><span>Jul</span><span>Okt</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="mt-2 text-[10px] text-gray-500 dark:text-gray-400 italic">
                  Geser dan ubah warna di atas — preview ini akan mengikuti secara realtime sebelum Anda klik Simpan.
                </p>
              </div>
            </div>

            {/* Text Color Settings Section */}
            <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5 text-gray-600" />
                Warna Teks
              </h3>

              {/* Theme selector for per-theme override */}
              <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40">
                <label className="block text-xs font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300 mb-2">
                  Edit konfigurasi warna untuk theme:
                </label>
                <select
                  value={editingTheme}
                  onChange={(e) => {
                    const t = e.target.value;
                    setEditingTheme(t);
                    // Load ALL color fields from selected theme's override.
                    // Fallback chain: theme_overrides[t][f] -> baseline root snapshot -> current form state
                    // We MUST NOT fall back to current branding[f] first because that holds the colors of
                    // the *previously-edited* theme, which would visually bleed into the new theme.
                    const ov = (branding.theme_overrides || {})[t] || {};
                    const baseline = rootBaselineRef.current || {};
                    const F = [
                      'primary_color','secondary_color',
                      'primary_text_color','sidebar_text_color','header_text_color',
                      'sidebar_color','sidebar_gradient_start','sidebar_gradient_end','sidebar_use_gradient',
                      'header_color','header_gradient_start','header_gradient_end','header_use_gradient',
                      'content_bg_color','content_bg_gradient_start','content_bg_gradient_end','content_use_gradient',
                      'text_color',
                      'logo_url','logo_portal_url','logo_invoice_url','logo_scale',
                    ];
                    F.forEach((f) => {
                      let v;
                      if (ov[f] !== undefined && ov[f] !== null && ov[f] !== '') v = ov[f];
                      else if (baseline[f] !== undefined && baseline[f] !== null && baseline[f] !== '') v = baseline[f];
                      else v = branding[f];
                      handleChange(f, v);
                    });
                  }}
                  className="w-full px-3 py-2 rounded border border-blue-300 bg-white dark:bg-slate-800 text-sm font-medium"
                  data-testid="editing-theme-select"
                >
                  {EDIT_THEMES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
                <div className="flex items-center justify-between mt-2 gap-2">
                  <p className="text-[10px] text-blue-600 dark:text-blue-400 flex-1">
                    Semua warna (background, header, sidebar, teks, gradient, accent) akan tersimpan hanya untuk theme ini.
                  </p>
                  <button
                    type="button"
                    onClick={handleResetTheme}
                    disabled={loading}
                    title={`Hapus override untuk tema "${editingTheme}" — kembalikan ke tampilan bawaan`}
                    className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-300 dark:border-red-800/50"
                    data-testid="reset-theme-button"
                  >
                    <RotateCcw className="w-3 h-3" /> Reset ke Bawaan
                  </button>
                </div>
              </div>

              {/* Text Color Presets */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Pilih Preset Warna Teks
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { name: 'Hitam', value: '#000000', icon: '⬛' },
                    { name: 'Abu Gelap', value: '#1f2937', icon: '🔲' },
                    { name: 'Abu', value: '#4b5563', icon: '◻️' },
                    { name: 'Putih', value: '#ffffff', icon: '⬜' },
                    { name: 'Biru Gelap', value: '#1e3a8a', icon: '🔵' },
                    { name: 'Hijau Gelap', value: '#14532d', icon: '🟢' },
                    { name: 'Merah Gelap', value: '#7f1d1d', icon: '🔴' },
                    { name: 'Ungu Gelap', value: '#4c1d95', icon: '🟣' },
                  ].map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      onClick={() => handleChange('text_color', preset.value)}
                      className={`p-2 rounded-lg border-2 transition-all text-xs ${
                        branding.text_color === preset.value
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <span>{preset.icon}</span>
                        <span className="truncate text-gray-900 dark:text-white">{preset.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Warna Teks Utama (Custom)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={branding.text_color || '#1f2937'}
                      onChange={(e) => handleChange('text_color', e.target.value)}
                      className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                    />
                    <Input
                      value={branding.text_color || '#1f2937'}
                      onChange={(e) => handleChange('text_color', e.target.value)}
                      placeholder="#1f2937"
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Warna Teks Sidebar
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={branding.sidebar_text_color || '#ffffff'}
                      onChange={(e) => handleChange('sidebar_text_color', e.target.value)}
                      className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                    />
                    <Input
                      value={branding.sidebar_text_color || '#ffffff'}
                      onChange={(e) => handleChange('sidebar_text_color', e.target.value)}
                      placeholder="#ffffff"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Warna Teks Header
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={branding.header_text_color || '#ffffff'}
                      onChange={(e) => handleChange('header_text_color', e.target.value)}
                      className="w-16 h-10 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
                    />
                    <Input
                      value={branding.header_text_color || '#ffffff'}
                      onChange={(e) => handleChange('header_text_color', e.target.value)}
                      placeholder="#ffffff"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Text Color Preview */}
              <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Preview Warna Teks:</p>
                <div className="flex gap-4">
                  <div 
                    className="p-3 rounded-lg flex-1"
                    style={{ 
                      backgroundColor: branding.content_bg_color || '#f8fafc',
                      color: branding.text_color || '#1f2937'
                    }}
                  >
                    <p className="font-medium">Teks Konten</p>
                    <p className="text-sm">Ini contoh teks di area konten</p>
                  </div>
                  <div 
                    className="p-3 rounded-lg"
                    style={{ 
                      backgroundColor: branding.sidebar_color || '#1e293b',
                      color: branding.sidebar_text_color || '#ffffff'
                    }}
                  >
                    <p className="font-medium">Sidebar</p>
                    <p className="text-sm">Menu</p>
                  </div>
                  <div 
                    className="p-3 rounded-lg"
                    style={{ 
                      backgroundColor: branding.header_color || '#f97316',
                      color: branding.header_text_color || '#ffffff'
                    }}
                  >
                    <p className="font-medium">Header</p>
                    <p className="text-sm">Navbar</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  No. Telepon
                </label>
                <Input
                  value={branding.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="021-1234567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </label>
                <Input
                  type="email"
                  value={branding.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="info@company.com"
                />
              </div>
            </div>

            {/* Website */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Website
              </label>
              <Input
                type="url"
                value={branding.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://www.company.com"
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Alamat
              </label>
              <textarea
                value={branding.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="Jl. Contoh No. 123, Jakarta"
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Tagline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Tagline/Slogan
              </label>
              <Input
                value={branding.tagline}
                onChange={(e) => handleChange('tagline', e.target.value)}
                placeholder="Internet Cepat & Terpercaya"
              />
            </div>

            {/* Preview Toggle */}
            <div className="border-t pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPreview(!preview)}
                className="w-full mb-4"
              >
                <Eye className="w-4 h-4 mr-2" />
                {preview ? 'Hide Preview' : 'Show Preview'}
              </Button>

              {preview && (
                <div className="p-4 border-2 border-dashed rounded-lg bg-gray-50">
                  <div 
                    className="p-6 rounded-lg text-white"
                    style={{ background: `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)` }}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      {branding.logo_url && (
                        <img src={branding.logo_url} alt="Logo" className="w-16 h-16 bg-white p-2 rounded" />
                      )}
                      <div>
                        <h3 className="text-2xl font-bold">{branding.company_name || 'Nama Perusahaan'}</h3>
                        {branding.tagline && (
                          <p className="text-sm opacity-90">{branding.tagline}</p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1 text-sm opacity-90">
                      {branding.phone && <p>📞 {branding.phone}</p>}
                      {branding.email && <p>✉️ {branding.email}</p>}
                      {branding.website && <p>🌐 {branding.website}</p>}
                      {branding.address && <p>📍 {branding.address}</p>}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Preview: Branding akan muncul di dashboard, invoice, dan halaman lainnya
                  </p>
                </div>
              )}
            </div>

            {/* Save Button */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1"
                style={{ 
                  backgroundColor: branding.primary_color,
                  color: 'white'
                }}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Menyimpan...' : 'Simpan Branding'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h4 className="font-semibold text-blue-900 mb-2">ℹ️ Tentang White Label</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Logo, warna, dan info kontak akan tampil di dashboard Anda</li>
            <li>• Branding akan muncul di invoice/receipt untuk pelanggan</li>
            <li>• Setiap perubahan akan langsung terlihat setelah refresh halaman</li>
            <li>• Pastikan URL logo dapat diakses secara publik</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default WhiteLabelSettings;
